import * as turf from "@turf/turf";
import { get, writable } from "svelte/store";
import { MapillaryManager } from "../../map/data/mapillary/MapillaryManager";
import { ToolBoxManager } from "../../map/controls/toolbox/ToolBoxManager";
import { MaxSpeedToolManager } from "../../map/controls/maxspeeds/MaxSpeedToolManager";
import { LayersToolBoxManager } from "../../map/controls/base-layers/LayersToolBoxManager";
import { appManager } from "../../AppManager";
import { MaplibreUtils } from "../../shared/MaplibreUtils";
import StyleParts from "../../shared/StyleParts";
import { RoutingManager } from "./routing/RoutingManager";
import { NavigationToolManager } from "../../map/controls/navigation/NavigationToolManager";
import { CommitsToolBoxManager } from "../../map/controls/commits/CommitsToolBoxManager";
import { ScenarioShareToolBoxManager } from "../../map/controls/sharing/ScenarioShareToolBoxManager";
import LeftDrivingCountry from "../../shared/LeftDrivingCountry";
import { UrlHashHandler } from "../../shared/UrlHashHandler";
import { CompareToolBoxManager } from "../../map/comparison/CompareToolBoxManager";
import { TripsScenarioManager } from "../../map/controls/layers/trips/TripsScenarioManager";
import { MapManager } from "../../lib/maplibre/MapManager";
export class TestManager {
    constructor(parent, options) {
        this.tripsScenarioManagersStore = writable([]);
        this.isLeftSideDriveStore = writable(false);
        this.mapViewStore = writable(undefined);
        this.customSourceId = "anyways-app";
        this.referenceBranchId = writable(undefined);
        this.referenceBranchCommitId = writable(undefined);
        this.mapHashHandler = new UrlHashHandler("map");
        this.parent = parent;
        this.options = options;
        this.mapillaryManager = new MapillaryManager({
            apiKey: options.mapillaryApiKey
        });
        this.navigationToolBoxManager = new ToolBoxManager({
            position: "top-right",
            visible: true,
            initialView: "default"
        });
        this.navigationToolManager = new NavigationToolManager();
        this.maxSpeedToolManager = new MaxSpeedToolManager(this);
        this.toolBoxManager = new ToolBoxManager({
            position: "top-right",
            visible: true,
            initialView: "none"
        });
        this.layersToolBoxManager = new LayersToolBoxManager(this.toolBoxManager, this.mapillaryManager);
        this.commitsToolBoxManager = new CommitsToolBoxManager(this, this.toolBoxManager);
        this.scenarioShareButtonToolBoxManager = new ScenarioShareToolBoxManager(this, this.toolBoxManager);
        this.compareToolBoxManager = new CompareToolBoxManager(this, this.toolBoxManager);
        this.routingManager = new RoutingManager(this, {
            profiles: this.options.profiles,
            settings: [
                {
                    profile: "bigtruck.fast",
                    color: "#ff0303",
                    visible: false,
                },
                {
                    profile: "car.fast",
                    color: "#425577",
                    visible: true,
                },
                {
                    profile: "bicycle.commute",
                    color: "#d9a300",
                    visible: true,
                },
                {
                    profile: "pedestrian.short",
                    color: "#88c3a1",
                    visible: false,
                },
            ]
        });
        this.parent.getTripScenariosStore().subscribe(tripScenariosData => {
            this.tripsScenarioManagersStore.set(tripScenariosData.map(d => {
                return new TripsScenarioManager(this, d, {
                    profiles: this.options.profiles
                });
            }));
        });
        this.tripsScenarioManagersStore.subscribe(async (managers) => {
            if (typeof this.map === "undefined") {
                await this.initializeMap();
            }
            else {
                managers.forEach(m => m.addToMap(this.map));
            }
        });
        this.parent.getBranchCommitId().subscribe(async (v) => {
            if (typeof this.map === "undefined") {
                await this.initializeMap();
            }
            else {
                this.map.updateCustomSource({
                    sourceId: this.customSourceId,
                    source: {
                        type: "vector",
                        url: appManager.api.publish.buildBranchCommitMvt(v),
                    },
                });
            }
        });
        this.parent.getData().subscribe(async () => {
            await this.initializeMap();
        });
        this.referenceBranchId.subscribe(async (referenceBranchId) => {
            if (typeof referenceBranchId === "undefined") {
                this.referenceBranchCommitId.set(undefined);
                return;
            }
            this.referenceBranchCommitId.set(await appManager.api.edit.getLatestCommitForBranch({
                accessToken: await appManager.api.getAccessToken(),
                branchId: referenceBranchId
            }));
        });
    }
    async initialize(mapContainer) {
        this.mapContainer = mapContainer;
        await this.initializeMap();
    }
    getTripsScenarioManagersStore() {
        return this.tripsScenarioManagersStore;
    }
    getCustomSourceId() {
        return this.customSourceId;
    }
    getViewStore() {
        return this.parent.getViewStore();
    }
    getMapViewStore() {
        return this.mapViewStore;
    }
    getMaxSpeedToolManager() {
        return this.maxSpeedToolManager;
    }
    getMapillaryMapManager() {
        return this.mapillaryManager;
    }
    getRoutingManger() {
        return this.routingManager;
    }
    getToolBoxManager() {
        return this.toolBoxManager;
    }
    getLayersToolBoxManager() {
        return this.layersToolBoxManager;
    }
    getCommitsToolBoxManager() {
        return this.commitsToolBoxManager;
    }
    getCompareToolBoxManager() {
        return this.compareToolBoxManager;
    }
    getScenarioShareToolBoxManager() {
        return this.scenarioShareButtonToolBoxManager;
    }
    getNavigationToolBoxManager() {
        return this.navigationToolBoxManager;
    }
    getIsLeftSideDriveStore() {
        return this.isLeftSideDriveStore;
    }
    getNavigationToolManager() {
        return this.navigationToolManager;
    }
    getBranchCommitIdStore() {
        return this.parent.getBranchCommitId();
    }
    getReferenceBranchIdStore() {
        return this.referenceBranchId;
    }
    getScenarioDataStore() {
        return this.parent.getData();
    }
    getOtherScenariosStore() {
        return this.parent.getOtherScenarios();
    }
    async initializeMap() {
        if (typeof this.map !== "undefined")
            return;
        if (typeof this.mapContainer === "undefined")
            return;
        const branchCommitId = get(this.parent.getBranchCommitId());
        if (typeof branchCommitId === "undefined")
            return;
        const data = get(this.parent.getData());
        if (typeof data === "undefined")
            return;
        const sourceUrl = appManager.api.publish.buildBranchCommitMvt(branchCommitId);
        const baseLayer = get(this.layersToolBoxManager.getActiveBaseLayerStore());
        let zoom = 10.51;
        const area = data.project.area;
        const bbox = turf.bbox(area);
        let center = turf.center(area).geometry.coordinates;
        let bounds;
        // get map state if present, otherwise use the scenario bbox.
        const mapState = this.mapHashHandler.getState();
        if (typeof mapState !== "undefined") {
            const parts = mapState.split("/");
            if (parts.length === 3) {
                center = [parseFloat(parts[1]), parseFloat(parts[2])];
                zoom = parseFloat(parts[0]);
            }
        }
        else {
            bounds = [[bbox[0], bbox[1]], [bbox[2], bbox[3]]];
        }
        this.map = new MapManager({
            imageryLayers: [
                {
                    source: {
                        type: "raster",
                        tiles: [
                            `https://api.maptiler.com/tiles/satellite-v2/{z}/{x}/{y}.jpg?key=${this.options.maptilerApiKey}`
                        ],
                        tileSize: 256,
                        attribution: "MapTiler",
                    },
                    layer: {
                        id: "background-imagery",
                        type: "raster",
                        source: "background-imagery-source",
                        minzoom: 0,
                        maxzoom: 20,
                        layout: {
                            visibility: "none",
                        },
                    },
                },
            ],
            baseLayer: baseLayer.layer,
            customSource: {
                sourceId: this.customSourceId,
                source: {
                    type: "vector",
                    url: sourceUrl,
                },
            }
        });
        await this.map.initialize(this.mapContainer, (e, map) => {
            console.log("this.map.initialize callback");
            return this.onLoad(e, map);
        }, {
            center: center,
            zoom: zoom,
            bounds: bounds
        });
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async onLoad(e, map) {
        console.log("before left");
        this.isLeftSideDriveStore.set(LeftDrivingCountry.isInLeftDrivingCountry(this.map.getView().center));
        StyleParts.addDetoursLayer(this.map, this.customSourceId);
        StyleParts.addCycleStreets(this.map, this.customSourceId);
        StyleParts.addConstructionWorksLayer(this.map, this.customSourceId);
        StyleParts.addZonesLayer(this.map, this.customSourceId);
        StyleParts.addRoadColorLayer(this.map, this.customSourceId);
        this.mapillaryManager.addToMap(this.map);
        this.navigationToolBoxManager.addToMap(this.map);
        this.navigationToolManager.addToMap(this.map);
        this.toolBoxManager.addToMap(this.map);
        this.maxSpeedToolManager.addToMap(this.map);
        this.layersToolBoxManager.addToMap(this.map);
        this.commitsToolBoxManager.addToMap(this.map);
        console.log("before tripsScenarioManagersStore");
        get(this.tripsScenarioManagersStore).forEach(m => m.addToMap(this.map));
        console.log("before routingManager");
        this.routingManager.addToMap(this.map);
        let viewIsBeingSet = false;
        viewIsBeingSet = true;
        this.mapViewStore.set(this.map.getView());
        viewIsBeingSet = false;
        this.map.on("zoomend", () => {
            viewIsBeingSet = true;
            this.mapViewStore.set(this.map.getView());
            viewIsBeingSet = false;
            this.isLeftSideDriveStore.set(LeftDrivingCountry.isInLeftDrivingCountry(this.map.getView().center));
        });
        this.map.on("dragend", () => {
            viewIsBeingSet = true;
            this.mapViewStore.set(this.map.getView());
            viewIsBeingSet = false;
            this.isLeftSideDriveStore.set(LeftDrivingCountry.isInLeftDrivingCountry(this.map.getView().center));
        });
        console.log("this.map.on(click, e => this.onMapClick(e));");
        this.map.on("click", e => this.onMapClick(e));
        this.map.on("mousemove", e => this.onMapMouseOver(e));
        this.mapViewStore.subscribe(v => {
            if (typeof v === "undefined")
                return;
            this.mapHashHandler.update(`${v.zoom.toFixed(2)}/${v.center.lng.toFixed(5)}/${v.center.lat.toFixed(5)}`, true);
            if (viewIsBeingSet)
                return;
            const currentView = this.map.getView();
            if (currentView.center.lat == v.center.lat &&
                currentView.center.lng == v.center.lng &&
                currentView.zoom == v.zoom)
                return;
            this.map.setView(v.zoom, v.center);
        });
        // TODO: move this to the cycling layer itself.
        // The 'cycling'-layer needs a few extra images 
        try {
            await MaplibreUtils.loadImage(map, appManager.PublicUrl + "/img/icons/arrow-8.png", "single-arrow");
            await MaplibreUtils.loadImage(map, appManager.PublicUrl + "/img/icons/double-arrow-8.png", "double-arrow");
        }
        catch (e) {
            console.error("Could not load a needed image:", e);
        }
    }
    onMapClick(e) {
        console.log(e);
        // see if the mapillary component can do something with this click.
        const mapillaryResponse = this.mapillaryManager.onMapClick(e);
        if (mapillaryResponse)
            return;
        // see if the routing component can do something with this click.
        const routingResponse = this.routingManager.onMapClick(e);
    }
    onMapMouseOver(e) {
        // see if the mapillary component can do something with this mouseover.
        const mapillaryResponse = this.mapillaryManager.onMapMouseOver(e);
        if (mapillaryResponse)
            return;
    }
    /**
     * Gets the reference branch commit id store
     *
     * @returns The branch commit id store.
     */
    getReferenceBranchCommitIdStore() {
        return this.referenceBranchCommitId;
    }
}
